import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as adminApi from '../../api/admin';
import styles from './AdminUserModal.module.css';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU');
};

const formatDateForInput = (iso) => {
  if (!iso) return '';
  return new Date(iso).toISOString().split('T')[0];
};

const SUBSCRIPTION_STATUS_LABELS = {
  Active: 'Активна',
  PastDue: 'Просрочена (списания продолжаются)',
  Cancelled: 'Отменена',
  Rejected: 'Отклонена',
  Expired: 'Завершена',
};

const formatAmount = (amount, currency) =>
  amount != null ? `${amount.toLocaleString('ru-RU')} ${currency === 'RUB' || !currency ? '₽' : currency}` : '—';

const AdminUserModal = ({ user, token, onClose, onUpdate, onRefresh }) => {
  const { user: currentAdmin } = useAuth();

  const [tab, setTab] = useState('info'); // 'info' | 'edit' | 'subscription' | 'actions'
  const [form, setForm] = useState({
    email: user.email || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    patronymic: user.patronymic || '',
    gender: user.gender || '',
    date_of_birth: formatDateForInput(user.date_of_birth),
    height_cm: user.height_cm || '',
    weight_kg: user.weight_kg || '',
    phone: user.phone || '',
    telegram_username: user.telegram_username || '',
    pay_until: formatDateForInput(user.pay_until),
  });
  const [blockType, setBlockType] = useState('permanent');
  const [blockedUntil, setBlockedUntil] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordSet, setPasswordSet] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [subscriptions, setSubscriptions] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState('');

  // Закрытие по Escape
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onClose]);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const loadSubscriptions = useCallback(async () => {
    setSubsLoading(true);
    setSubsError('');
    try {
      const data = await adminApi.getUserSubscriptions(token, user.id);
      setSubscriptions(data.subscriptions);
    } catch (err) {
      setSubsError(err.response?.data?.message || 'Не удалось загрузить подписки');
    } finally {
      setSubsLoading(false);
    }
  }, [token, user.id]);

  // Подписки живут в CloudPayments — грузим при первом открытии вкладки
  useEffect(() => {
    if (tab === 'subscription' && subscriptions === null && !subsLoading && !subsError) {
      loadSubscriptions();
    }
  }, [tab, subscriptions, subsLoading, subsError, loadSubscriptions]);

  const handleCancelSubscriptions = async ({ subscriptionId, revokeAccess }) => {
    const confirmText = revokeAccess
      ? 'Отменить все активные подписки и закрыть доступ прямо сейчас? Пользователь будет исключён из чата.'
      : subscriptionId
      ? `Отменить подписку ${subscriptionId}? Доступ сохранится до конца оплаченного периода.`
      : 'Отключить автопродление (отменить все активные подписки)? Доступ сохранится до конца оплаченного периода.';
    if (!window.confirm(confirmText)) return;

    const actionName = revokeAccess ? 'sub_revoke' : subscriptionId ? `sub_${subscriptionId}` : 'sub_all';
    setActionLoading(actionName);
    try {
      const data = await adminApi.cancelUserSubscriptions(token, user.id, {
        subscription_id: subscriptionId,
        revoke_access: revokeAccess,
      });
      showMessage(data.message);
      if (data.access_revoked) {
        onUpdate({ ...user, pay_until: data.pay_until });
        onRefresh();
      }
    } catch (err) {
      showMessage(err.response?.data?.message || 'Ошибка отмены подписки', 'error');
    } finally {
      setActionLoading('');
      loadSubscriptions();
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const result = await adminApi.updateUser(token, user.id, form);
      onUpdate(result.user);
      showMessage('Профиль обновлён');
    } catch (err) {
      showMessage(err.response?.data?.message || 'Ошибка сохранения', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const doAction = async (actionName, apiFn, successMsg) => {
    setActionLoading(actionName);
    try {
      await apiFn();
      showMessage(successMsg);
      onRefresh();
      onClose();
    } catch (err) {
      showMessage(err.response?.data?.message || 'Ошибка', 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleBlock = () => {
    if (blockType === 'temporary' && !blockedUntil) {
      showMessage('Укажите дату блокировки', 'error');
      return;
    }
    doAction(
      'block',
      () => adminApi.blockUser(token, user.id, blockType, blockedUntil || undefined),
      'Пользователь заблокирован'
    );
  };

  const handleUnblock = () =>
    doAction('unblock', () => adminApi.unblockUser(token, user.id), 'Пользователь разблокирован');

  const handleForceLogout = () =>
    doAction('logout', () => adminApi.forceLogout(token, user.id), 'Сессии завершены');

  const handleResetPassword = () =>
    doAction('reset', () => adminApi.resetPassword(token, user.id), 'Установлен флаг смены пароля');

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pass = '';
    for (let i = 0; i < 10; i++) pass += chars[Math.floor(Math.random() * chars.length)];
    setNewPassword(pass);
    setPasswordSet('');
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showMessage('Пароль должен быть не менее 6 символов', 'error');
      return;
    }
    setActionLoading('changePass');
    try {
      await adminApi.changePassword(token, user.id, newPassword);
      setPasswordSet(newPassword);
      setNewPassword('');
      showMessage('Пароль успешно изменён');
      onRefresh();
    } catch (err) {
      showMessage(err.response?.data?.message || 'Ошибка', 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(passwordSet);
    showMessage('Пароль скопирован в буфер обмена');
  };

  const handleSetAdmin = (isAdmin) =>
    doAction(
      'admin',
      () => adminApi.setAdmin(token, user.id, isAdmin),
      isAdmin ? 'Пользователь назначен администратором' : 'Роль администратора снята'
    );

  const activeSubsCount = subscriptions?.filter((s) => s.is_active).length ?? 0;
  const isBlocked = user.is_blocked || (user.blocked_until && new Date(user.blocked_until) > new Date());
  const isSelf = user.id === currentAdmin?.id;

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>{user.email}</h2>
            <div className={styles.modalMeta}>
              ID: <span className={styles.userId}>{user.id}</span>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Сообщение */}
        {message.text && (
          <div className={`${styles.message} ${styles[message.type]}`}>
            {message.text}
          </div>
        )}

        {/* Табы */}
        <div className={styles.tabs}>
          {['info', 'edit', 'subscription', 'actions'].map((t) => (
            <button
              key={t}
              className={`${styles.tab} ${tab === t ? styles.activeTab : ''}`}
              onClick={() => setTab(t)}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        <div className={styles.modalBody}>
          {/* ---- Вкладка: Информация ---- */}
          {tab === 'info' && (
            <div className={styles.infoGrid}>
              <InfoRow label="Email" value={user.email} />
              <InfoRow label="Имя" value={[user.first_name, user.last_name, user.patronymic].filter(Boolean).join(' ')} />
              <InfoRow label="Телефон" value={user.phone} />
              <InfoRow label="Telegram" value={user.telegram_username} />
              <InfoRow label="Оплачено до" value={user.pay_until ? new Date(user.pay_until).toLocaleDateString('ru-RU') : null} />
              <InfoRow label="Пол" value={user.gender === 'male' ? 'Мужской' : user.gender === 'female' ? 'Женский' : null} />
              <InfoRow label="Дата рождения" value={formatDateTime(user.date_of_birth)?.split(',')[0]} />
              <InfoRow label="Рост" value={user.height_cm ? `${user.height_cm} см` : null} />
              <InfoRow label="Вес" value={user.weight_kg ? `${user.weight_kg} кг` : null} />
              <InfoRow label="Роль" value={user.is_admin ? 'Администратор' : 'Пользователь'} />
              <InfoRow label="Статус" value={user.is_blocked ? 'Заблокирован (постоянно)' : user.blocked_until && new Date(user.blocked_until) > new Date() ? `Заблокирован до ${formatDateTime(user.blocked_until)}` : 'Активен'} />
              <InfoRow label="Смена пароля при входе" value={user.force_password_change ? 'Да ⚠' : 'Нет'} />
              <InfoRow label="Дата регистрации" value={formatDateTime(user.registered_at)} />
              <InfoRow label="Последняя смена пароля" value={formatDateTime(user.password_changed_at)} />
            </div>
          )}

          {/* ---- Вкладка: Редактирование ---- */}
          {tab === 'edit' && (
            <form onSubmit={handleSaveProfile} className={styles.editForm}>
              <div className={styles.formGrid}>
                <Field label="Email">
                  <input name="email" value={form.email} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Имя">
                  <input name="first_name" value={form.first_name} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Фамилия">
                  <input name="last_name" value={form.last_name} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Отчество">
                  <input name="patronymic" value={form.patronymic} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Телефон">
                  <input name="phone" value={form.phone} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Telegram">
                  <input name="telegram_username" value={form.telegram_username} onChange={handleFormChange} className={styles.input} placeholder="@username" />
                </Field>
                <Field label="Оплачено до">
                  <input type="date" name="pay_until" value={form.pay_until} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Пол">
                  <select name="gender" value={form.gender} onChange={handleFormChange} className={styles.input}>
                    <option value="">Не указан</option>
                    <option value="male">Мужской</option>
                    <option value="female">Женский</option>
                  </select>
                </Field>
                <Field label="Дата рождения">
                  <input type="date" name="date_of_birth" value={form.date_of_birth} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Рост (см)">
                  <input type="number" name="height_cm" value={form.height_cm} onChange={handleFormChange} className={styles.input} />
                </Field>
                <Field label="Вес (кг)">
                  <input type="number" step="0.1" name="weight_kg" value={form.weight_kg} onChange={handleFormChange} className={styles.input} />
                </Field>
              </div>
              <button type="submit" className={styles.saveBtn} disabled={isSaving}>
                {isSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          )}

          {/* ---- Вкладка: Подписка ---- */}
          {tab === 'subscription' && (
            <div className={styles.actions}>
              <div className={styles.actionGroup}>
                <h3 className={styles.actionGroupTitle}>Подписки CloudPayments</h3>
                <p className={styles.hint}>
                  Оплачено до: {user.pay_until ? new Date(user.pay_until).toLocaleDateString('ru-RU') : '—'}
                </p>

                {subsLoading && <p className={styles.hint}>Загрузка...</p>}
                {subsError && !subsLoading && (
                  <>
                    <p className={styles.hint}>⚠ {subsError}</p>
                    <button className={`${styles.actionBtn} ${styles.neutralBtn}`} onClick={loadSubscriptions}>
                      Повторить
                    </button>
                  </>
                )}
                {!subsLoading && !subsError && subscriptions?.length === 0 && (
                  <p className={styles.hint}>У пользователя нет подписок в CloudPayments</p>
                )}

                {!subsLoading && !subsError && subscriptions?.length > 0 && (
                  <>
                    {activeSubsCount > 1 && (
                      <p className={styles.hint}>
                        ⚠ Активных подписок: {activeSubsCount} — пользователь платит несколько раз за период
                      </p>
                    )}
                    <div className={styles.subsList}>
                      {subscriptions.map((sub) => (
                        <div key={sub.id} className={`${styles.subCard} ${sub.is_active ? '' : styles.subInactive}`}>
                          <div className={styles.subHeader}>
                            <span className={styles.subStatus}>
                              {sub.is_active ? '🟢' : '⚪'} {SUBSCRIPTION_STATUS_LABELS[sub.status] || sub.status}
                            </span>
                            <span className={styles.subAmount}>{formatAmount(sub.amount, sub.currency)}</span>
                          </div>
                          <div className={styles.subMeta}>
                            <span>ID: <span className={styles.userId}>{sub.id}</span></span>
                            <span>Создана: {formatDateTime(sub.created_at)}</span>
                            {sub.is_active && <span>Следующее списание: {formatDateTime(sub.next_charge_at)}</span>}
                            {sub.successful_payments != null && <span>Успешных платежей: {sub.successful_payments}</span>}
                          </div>
                          {sub.is_active && (
                            <button
                              className={`${styles.actionBtn} ${styles.blockBtn}`}
                              onClick={() => handleCancelSubscriptions({ subscriptionId: sub.id })}
                              disabled={!!actionLoading}
                            >
                              {actionLoading === `sub_${sub.id}` ? 'Отмена...' : 'Отменить эту подписку'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {!subsLoading && !subsError && subscriptions !== null && (
                <div className={styles.actionGroup}>
                  <h3 className={styles.actionGroupTitle}>Отмена</h3>
                  <button
                    className={`${styles.actionBtn} ${styles.blockBtn}`}
                    onClick={() => handleCancelSubscriptions({})}
                    disabled={!!actionLoading || activeSubsCount === 0}
                  >
                    {actionLoading === 'sub_all' ? 'Отмена...' : 'Отключить автопродление'}
                  </button>
                  <p className={styles.hint}>
                    Отменяет все активные подписки. Доступ и чат сохранятся до даты «Оплачено до».
                  </p>
                  <button
                    className={`${styles.actionBtn} ${styles.revokeBtn}`}
                    onClick={() => handleCancelSubscriptions({ revokeAccess: true })}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === 'sub_revoke' ? 'Выполняется...' : 'Отменить и закрыть доступ'}
                  </button>
                  <p className={styles.hint}>
                    Отменяет все активные подписки, ставит «Оплачено до» на текущий момент и исключает из чата.
                    Если какую-то подписку отменить не удалось — доступ не закрывается.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ---- Вкладка: Действия ---- */}
          {tab === 'actions' && (
            <div className={styles.actions}>
              {/* Блокировка */}
              <div className={styles.actionGroup}>
                <h3 className={styles.actionGroupTitle}>Блокировка</h3>
                {isBlocked ? (
                  <button
                    className={`${styles.actionBtn} ${styles.unblockBtn}`}
                    onClick={handleUnblock}
                    disabled={actionLoading === 'unblock' || isSelf}
                  >
                    {actionLoading === 'unblock' ? 'Разблокировка...' : 'Разблокировать'}
                  </button>
                ) : (
                  <div className={styles.blockForm}>
                    <div className={styles.blockTypeRow}>
                      <label className={styles.radioLabel}>
                        <input type="radio" name="blockType" value="permanent" checked={blockType === 'permanent'} onChange={() => setBlockType('permanent')} />
                        Постоянная
                      </label>
                      <label className={styles.radioLabel}>
                        <input type="radio" name="blockType" value="temporary" checked={blockType === 'temporary'} onChange={() => setBlockType('temporary')} />
                        Временная
                      </label>
                    </div>
                    {blockType === 'temporary' && (
                      <input
                        type="datetime-local"
                        className={styles.input}
                        value={blockedUntil}
                        onChange={(e) => setBlockedUntil(e.target.value)}
                      />
                    )}
                    <button
                      className={`${styles.actionBtn} ${styles.blockBtn}`}
                      onClick={handleBlock}
                      disabled={actionLoading === 'block' || isSelf}
                    >
                      {actionLoading === 'block' ? 'Блокировка...' : 'Заблокировать'}
                    </button>
                  </div>
                )}
                {isSelf && <p className={styles.hint}>Нельзя заблокировать собственный аккаунт</p>}
              </div>

              {/* Сессии */}
              <div className={styles.actionGroup}>
                <h3 className={styles.actionGroupTitle}>Управление сессиями</h3>
                <button
                  className={`${styles.actionBtn} ${styles.neutralBtn}`}
                  onClick={handleForceLogout}
                  disabled={actionLoading === 'logout'}
                >
                  {actionLoading === 'logout' ? 'Выполняется...' : 'Принудительный выход'}
                </button>
              </div>

              {/* Пароль */}
              <div className={styles.actionGroup}>
                <h3 className={styles.actionGroupTitle}>Пароль</h3>
                <button
                  className={`${styles.actionBtn} ${styles.neutralBtn}`}
                  onClick={handleResetPassword}
                  disabled={actionLoading === 'reset' || user.force_password_change}
                >
                  {actionLoading === 'reset'
                    ? 'Выполняется...'
                    : user.force_password_change
                    ? 'Флаг уже установлен'
                    : 'Потребовать смену пароля'}
                </button>
                <p className={styles.hint}>
                  Пользователь будет обязан сменить пароль при следующем входе
                </p>

                <div className={styles.passwordChangeBlock}>
                  <div className={styles.passwordInputRow}>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Новый пароль"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setPasswordSet(''); }}
                    />
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.neutralBtn}`}
                      onClick={generatePassword}
                      title="Сгенерировать пароль"
                    >
                      Сгенерировать
                    </button>
                  </div>
                  <button
                    className={`${styles.actionBtn} ${styles.adminBtn}`}
                    onClick={handleChangePassword}
                    disabled={actionLoading === 'changePass' || !newPassword}
                  >
                    {actionLoading === 'changePass' ? 'Сохранение...' : 'Установить пароль'}
                  </button>
                  {passwordSet && (
                    <div className={styles.passwordResult}>
                      <span className={styles.passwordValue}>{passwordSet}</span>
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.neutralBtn}`}
                        onClick={handleCopyPassword}
                      >
                        Скопировать
                      </button>
                    </div>
                  )}
                  <p className={styles.hint}>
                    Установите новый пароль и передайте его пользователю. При следующем входе он будет обязан его сменить.
                  </p>
                </div>
              </div>

              {/* Роль */}
              <div className={styles.actionGroup}>
                <h3 className={styles.actionGroupTitle}>Роль администратора</h3>
                {user.is_admin ? (
                  <button
                    className={`${styles.actionBtn} ${styles.dangerBtn}`}
                    onClick={() => handleSetAdmin(false)}
                    disabled={actionLoading === 'admin' || isSelf}
                  >
                    {actionLoading === 'admin' ? 'Выполняется...' : 'Снять роль администратора'}
                  </button>
                ) : (
                  <button
                    className={`${styles.actionBtn} ${styles.adminBtn}`}
                    onClick={() => handleSetAdmin(true)}
                    disabled={actionLoading === 'admin'}
                  >
                    {actionLoading === 'admin' ? 'Выполняется...' : 'Назначить администратором'}
                  </button>
                )}
                {isSelf && <p className={styles.hint}>Нельзя изменить собственную роль</p>}
              </div>

              {/* Telegram-чат */}
              <div className={styles.actionGroup}>
                <h3 className={styles.actionGroupTitle}>Telegram-чат</h3>
                <p className={styles.hint}>
                  Статус: {user.in_chat ? '✅ В чате' : '❌ Не в чате'}
                  {!user.telegram_username && ' · нет Telegram-ника'}
                </p>
                {user.in_chat ? (
                  <button
                    className={`${styles.actionBtn} ${styles.blockBtn}`}
                    onClick={() => doAction('chat_remove', () => adminApi.removeFromChat(token, user.id), 'Пользователь исключён из чата')}
                    disabled={actionLoading === 'chat_remove'}
                  >
                    {actionLoading === 'chat_remove' ? 'Выполняется...' : 'Исключить из чата'}
                  </button>
                ) : (
                  <button
                    className={`${styles.actionBtn} ${styles.unblockBtn}`}
                    onClick={() => doAction('chat_add', () => adminApi.addToChat(token, user.id), 'Ссылка-приглашение отправлена')}
                    disabled={actionLoading === 'chat_add' || !user.telegram_username}
                  >
                    {actionLoading === 'chat_add' ? 'Выполняется...' : 'Добавить в чат'}
                  </button>
                )}
                {!user.telegram_username && (
                  <p className={styles.hint}>Укажите Telegram-ник во вкладке «Редактировать»</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TAB_LABELS = {
  info: 'Информация',
  edit: 'Редактировать',
  subscription: 'Подписка',
  actions: 'Действия',
};

const InfoRow = ({ label, value }) => (
  <div className={styles.infoRow}>
    <span className={styles.infoLabel}>{label}</span>
    <span className={styles.infoValue}>{value || '—'}</span>
  </div>
);

const Field = ({ label, children }) => (
  <div className={styles.field}>
    <label className={styles.fieldLabel}>{label}</label>
    {children}
  </div>
);

export default AdminUserModal;
